import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import browserHistory from 'react-router/lib/browserHistory';
import get from 'lodash/get';

// Store
import { hideDetailsPopup, openDetailsPopup } from '../../redux/modules/propertyDetails';

// Helpers
import { ENTITY_TYPES, QUERY_PARAMS, ROUTES } from '../../constants';
import { getProjectDetailsPageUrl, getPropertyDetailsPageUrl } from '../../utils/getDetailsPageUrl';
import AnalyticsEventService from '../../services/AnalyticsEventService';

const withDetailsPopup = (WrappedComponent) => {
  class DetailsPopupContainer extends Component {
    getLocation = (isProject) =>
      isProject ? ROUTES.PROJECT_DETAILS : ROUTES.PROPERTY_DETAILS;

    logPropertyEvent = (id) => AnalyticsEventService.viewProperty(id);

    logProjectEvent = (id) => AnalyticsEventService.viewProject(id);

    navigateToDetailsPage = (event, route) => {
      if (event && (event.ctrlKey || event.metaKey)) {
        window.open(route);
        return true;
      }
      if (this.props.isMobile) {
        browserHistory.push(route);
        return true;
      }
      return false;
    };

    openDetailsModal = (slug, queryKey, popupData) => {
      const { pathname = '/', query = {} } =
        browserHistory.getCurrentLocation();
      const currentQuerySlug = get(query, queryKey);

      if (currentQuerySlug !== slug) {
        browserHistory.push({
          pathname,
          query: { ...query, [queryKey]: slug }
        });
      }

      if (!this.props.isDetailsModalOpen) {
        this.props.openDetailsPopup(popupData);
      }
    };

    openPropertyDetails = (event, slug, defaultData) => {
      const route = getPropertyDetailsPageUrl(slug, this.props.locale);

      if (this.navigateToDetailsPage(event, route)) return Promise.resolve(null);

      this.openDetailsModal(slug, QUERY_PARAMS.PROPERTY, {
        ...defaultData,
        slug,
        entityType: ENTITY_TYPES.PROPERTY
      });

      return this.logPropertyEvent(slug);
    };

    openProjectDetails = (event, id, defaultData) => {
      const route = getProjectDetailsPageUrl(id, this.props.locale);

      if (this.navigateToDetailsPage(event, route)) return Promise.resolve(null);

      this.openDetailsModal(id, QUERY_PARAMS.PROJECT, {
        ...defaultData,
        entityType: ENTITY_TYPES.PROJECT,
        propertyId: id
      });

      return this.logProjectEvent(id);
    };

    openPopupWithDetails = (
      event,
      slug,
      entityType = ENTITY_TYPES.PROPERTY,
      defaultData
    ) => {
      if (entityType === ENTITY_TYPES.PROJECT) {
        return this.openProjectDetails(event, slug, defaultData);
      }

      return this.openPropertyDetails(event, slug, defaultData);
    };

    closePopupWithDetails = () => {
      const { pathname = '/', query = {} } =
        browserHistory.getCurrentLocation();

      const hasPopupQuery =
        query[QUERY_PARAMS.PROPERTY] || query[QUERY_PARAMS.PROJECT];

      if (hasPopupQuery) {
        browserHistory.push({
          pathname,
          query: {
            ...query,
            [QUERY_PARAMS.PROJECT]: undefined,
            [QUERY_PARAMS.PROPERTY]: undefined
          }
        });
      }

      if (this.props.isDetailsModalOpen) {
        this.props.hideDetailsPopup();
      }
    };

    checkIsDetailsPopupOpen = () => {
      if (this.props.isMobile) {
        this.closePopupWithDetails();
        return;
      }

      const { query = {} } = browserHistory.getCurrentLocation();

      const propertySlug = get(query, QUERY_PARAMS.PROPERTY);

      if (propertySlug) {
        this.openPropertyDetails(null, propertySlug);
        return;
      }

      const projectSlug = get(query, QUERY_PARAMS.PROJECT);

      if (projectSlug) {
        this.openProjectDetails(null, projectSlug);
        return;
      }

      this.closePopupWithDetails();
    };

    render() {
      return (
        <WrappedComponent
          {...this.props}
          openPopupWithDetails={this.openPopupWithDetails}
          closePopupWithDetails={this.closePopupWithDetails}
          checkIsDetailsPopupOpen={this.checkIsDetailsPopupOpen}
        />
      );
    }
  }

  DetailsPopupContainer.propTypes = {
    isMobile: PropTypes.bool,
    locale: PropTypes.string,
    openDetailsPopup: PropTypes.func,
    hideDetailsPopup: PropTypes.func,
    isDetailsModalOpen: PropTypes.bool
  };

  const mapStateToProps = (state) => ({
    isMobile: state.windowDimentions.isMobile,
    locale: state.lang.locale,
    isDetailsModalOpen: state.propertyDetails.isDetailsModalOpen
  });

  const mapDispatchToProps = {
    openDetailsPopup,
    hideDetailsPopup
  };

  return connect(mapStateToProps, mapDispatchToProps)(DetailsPopupContainer);
};

export default withDetailsPopup;
